import type { RestangularService } from '../common/restangular.service'

import { app } from '../../app.module'

interface IUserData {
  userId: number
  name: string
  email: string
  pictureUrl: string
  isAdmin: boolean
}

/**
 * グループ設定
 */
class GroupSettingComponentCtrl {
  private groupId: number
  private selected: IUserData[] = []
  // ユーザーリスト
  private usersList: IUserData[]
  // データ取得中フラグ
  private isLoading: boolean
  // 対象グループの最大ユーザー数
  private maxUserCount: number
  // 管理者数
  private adminsCount: number
  // 追加できるユーザー数
  private remainUsersCount: number
  // 対象グループのユーザー数に対するアラート
  private usersCountAlert: string

  constructor(
    private $rootScope,
    private $scope: ng.IScope,
    $state,
    private $mdDialog: {
      show: Function
    },
    private DialogService,
    private RestangularService: RestangularService
  ) {
    this.groupId = $rootScope.groupId
    this.maxUserCount = $rootScope.groupMaxUserCount
    this.isLoading = true

    $scope.$watch(
      () => {
        return $rootScope.isGroupAdmin
      },
      () => {
        // グループの管理権限がない場合、投稿一覧に移動
        if ($rootScope.isGroupAdmin === false) {
          $state.go('post')
        }
      }
    )
  }

  /**
   * 初期化
   */
  public $onInit(): void {
    this.getGroupUsersList()
  }

  /**
   * 対象グループのユーザーリストを取得
   */
  public getGroupUsersList() {
    const endpoint = `group/users?group_id=${this.groupId}`

    this.RestangularService.request('GET', endpoint, null)
      .then((result) => {
        this.usersList = result[0].data
        // 管理者数をセット
        this.calculateAdminsCount()
        this.checkRemainUsersCount()
        this.isLoading = false
      })
      .catch(() => {
        this.isLoading = false
      })
  }

  /**
   * グループのユーザーを解除
   * @param {number} userId ユーザーID
   * @param {boolean} isAdmin 管理権限
   */
  public changeUserRole(userId: number, isAdmin: boolean) {
    const endpoint = '/group/userRole'

    this.RestangularService.request('UPDATE', endpoint, {
      groupId: this.groupId,
      userId: userId,
      isAdmin: isAdmin,
    })
      .then((response) => {
        // 保存成功の場合
        this.DialogService.showToast(response.message, 'success-toast')

        // 管理権限を変更すると、管理者数をアップデート
        this.calculateAdminsCount()
      })
      .catch((error) => {
        // 保存失敗の場合
        this.DialogService.showToast(error.data.error, 'error-toast')
      })
  }

  /**
   * グループのユーザーを解除
   * @param {number} userId ユーザーID
   */
  public removeUser(userId: number) {
    // confirmを出し、再確認する
    const answer = confirm(
      'ユーザーをグループから削除してよろしいですか? この操作は取り消しできません。'
    )

    if (answer !== true) {
      return
    }

    const endpoint = '/group/user'

    // ユーザー解除を行う
    this.RestangularService.request('DELETE', endpoint, {
      groupId: this.groupId,
      userId: userId,
    })
      .then((response) => {
        // 保存成功の場合、
        this.DialogService.showToast(response.message, 'success-toast')

        // ユーザーリストから解除されたユーザーを削除
        this.usersList = this.usersList.filter((user) => user.userId !== userId)
        // ユーザーが削除されたら管理者数をアップデート
        this.calculateAdminsCount()
        this.checkRemainUsersCount()
      })
      .catch((error) => {
        // 保存失敗の場合
        this.DialogService.showToast(error.data.error, 'error-toast')
      })
  }

  /**
   * 管理者数を計算
   */
  public calculateAdminsCount() {
    // 管理者数をセット
    this.adminsCount = this.usersList.filter((user) => {
      return user.isAdmin === true
    }).length
  }

  /**
   * 追加できる残りユーザー数をチェック
   */
  public checkRemainUsersCount() {
    // 追加できるユーザー数を計算
    this.remainUsersCount =
      this.maxUserCount > this.usersList.length ? this.maxUserCount - this.usersList.length : 0

    // 最大ユーザー数をまだ超えない場合
    if (this.remainUsersCount > 0) {
      this.usersCountAlert = `あと${this.remainUsersCount}人ユーザーを追加できます。`
    } else {
      this.usersCountAlert =
        'これ以上ユーザーを追加することができません。\nユーザーを追加したい場合は、サポートにお問い合わせください。'
    }
  }

  /**
   * ユーザーを招待するモーダルを開く
   */
  public showUserInviteModal() {
    this.$mdDialog
      .show({
        template: `<user-invite-modal></user-invite-modal>`,
        clickOutsideToClose: true,
      })
      .then(
        (userInfo) => {
          if (userInfo !== undefined) {
            // ユーザーリストにユーザーを追加(or削除)
            this.usersList.push(userInfo)
            this.calculateAdminsCount()
            this.checkRemainUsersCount()
          }
        },
        () => {
          return
        }
      )
  }
}

app.component('groupSetting', {
  template: require('./group-setting.pug')(),
  controller: GroupSettingComponentCtrl,
})
